import { useMemo, useState } from "react";
import { Namespace, useT } from "@racoon/i18n";
import { PhotoCarousel } from "./photoCarousel";
import { PhotoSlider } from "./photoSlider";
import type { JobPhotos } from "~/shared/utils";

/**
 * The three photo carousels of a job's photo report (before / after / no-export).
 *
 * The carousels share a single fullscreen slider: clicking any photo opens one
 * slider over every photo of the job, so the viewer can page from "before"
 * straight through to "after" instead of closing and reopening per group.
 */
export const JobPhotoReport = ({
  beforePhotos,
  afterPhotos,
  failureReasonPhotos,
  className = "flex lg:flex-row flex-col gap-4",
  showCarouselTitles = true,
}: JobPhotos & { className?: string; showCarouselTitles?: boolean }) => {
  const t = useT();
  const [sliderIndex, setSliderIndex] = useState<number | null>(null);

  const groups = useMemo(
    () => [
      { title: t(Namespace.Govdash, "wasteExport.photoBefore"), photos: beforePhotos },
      { title: t(Namespace.Govdash, "wasteExport.photoAfter"), photos: afterPhotos },
      ...(failureReasonPhotos.length > 0
        ? [
            {
              title: t(Namespace.Govdash, "wasteExport.noExportPhoto"),
              photos: failureReasonPhotos,
            },
          ]
        : []),
    ],
    [t, beforePhotos, afterPhotos, failureReasonPhotos],
  );

  const allPhotos = useMemo(() => groups.flatMap((group) => group.photos), [groups]);

  let offset = 0;
  const groupsWithOffset = groups.map((group) => {
    const groupOffset = offset;
    offset += group.photos.length;
    return { ...group, offset: groupOffset };
  });

  // The slider spans every group, so its title follows whichever group the
  // current slide falls into.
  const currentSlide = sliderIndex ?? 0;
  const currentGroup = groupsWithOffset
    .filter((group) => group.photos.length > 0)
    .reduce<
      (typeof groupsWithOffset)[number] | undefined
    >((found, group) => (currentSlide >= group.offset ? group : found), undefined);

  return (
    <div className={className}>
      {groupsWithOffset.map((group) => (
        <PhotoCarousel
          key={group.title}
          className="lg:flex-1 lg:basis-0"
          title={showCarouselTitles ? group.title : undefined}
          photos={group.photos}
          onPhotoClick={(index) => setSliderIndex(group.offset + index)}
        />
      ))}

      <PhotoSlider
        title={currentGroup?.title ?? t(Namespace.Govdash, "wasteExport.photoReport")}
        open={sliderIndex !== null}
        onOpenChange={(open) => !open && setSliderIndex(null)}
        photos={allPhotos}
        initialSlide={sliderIndex ?? 0}
        onSlideChange={setSliderIndex}
      />
    </div>
  );
};
